import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { CODE_LENGTH, OtpInput } from '@/ui/OtpInput'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/**
 * `/verify-mobile` — S-D2 screens 05 and 06.
 *
 * Reached straight from a mobile signup, which leaves the browser **signed in**
 * with an unverified number: `POST /auth/signup/mobile` sets the session cookie,
 * so this screen is behind a session even though it is in the public band of the
 * route table. It has to be reachable without the tenant guard — a brand-new
 * account has no organisation — and it has to survive a reload, which is why the
 * number comes from `/auth/me` rather than from navigation state.
 *
 * **Two failure screens, not one.** `OTP_INVALID` re-enables the boxes and keeps
 * what was typed, because the likeliest cause is one wrong digit. `OTP_EXPIRED`
 * clears them and offers only *Send a new code*, because retrying a dead code
 * cannot work and five wrong guesses spend it (`app/security/otp.py`). The API
 * separates the two codes for exactly this reason.
 */
export function VerifyMobile(): JSX.Element {
  const navigate = useNavigate()
  const { user, status, refresh } = useAuth()

  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)

  const verify = useMutation((body: { code: string }) =>
    call(() => api.POST('/api/v1/auth/mobile/verify', { body })),
  )
  const resend = useMutation(() => call(() => api.POST('/api/v1/auth/mobile/resend', {})))

  const expired = verify.state.failure?.code === 'OTP_EXPIRED'
  const wrong = verify.state.failure?.code === 'OTP_INVALID'

  // An expired code cannot be retried, so the boxes are cleared rather than left
  // holding digits whose only possible outcome is another refusal.
  useEffect(() => {
    if (expired) setCode('')
  }, [expired])

  // The "Resend in 0:42" S-D2 drew. Driven by the server's own `Retry-After`,
  // never by a client-side guess at the policy.
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => {
      setCooldown((seconds) => seconds - 1)
    }, 1000)
    return () => {
      window.clearTimeout(timer)
    }
  }, [cooldown])

  async function onSubmit(event?: SyntheticEvent): Promise<void> {
    event?.preventDefault()
    if (code.length !== CODE_LENGTH) return
    const result = await verify.run({ code })
    if (result.ok) {
      await refresh()
      void navigate(DEFAULT_HOME, { replace: true })
    }
  }

  async function onResend(): Promise<void> {
    setCode('')
    verify.reset()
    const result = await resend.run(undefined)
    setCooldown(
      result.ok
        ? RESEND_COOLDOWN_SECONDS
        : (result.failure.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS),
    )
  }

  if (status === 'anonymous') {
    return (
      <PublicPage title="Sign in to confirm your number">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            Confirming a mobile number needs the account it belongs to. Sign in and we will bring
            you back here.
          </p>
          <Link className="ar-btn ar-btn--primary" to="/login">
            Sign in
          </Link>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Enter the code"
      footer={
        <>
          Wrong number? <Link to="/settings/organisation">Change it in settings</Link>
        </>
      }
    >
      {/* `method="post"` so a submit with dead JavaScript cannot put this OTP in the
          query string. It is single-use and short-lived, which shortens the window
          without closing it. Reasoning in full on `Login.tsx`'s form. */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <p className="ar-muted" style={{ margin: 0 }}>
            We sent a {CODE_LENGTH}-digit code to <strong>{user?.mobile ?? 'your mobile'}</strong>.
            It expires ten minutes after it was sent.
          </p>

          {expired ? (
            <div className="ar-alert ar-alert--warning" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>This code has expired</strong>
                Ask for a new one — the old code will not work even if you type it correctly.
              </div>
            </div>
          ) : wrong ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>That code is not right</strong>
                Check the last message — codes are {CODE_LENGTH} digits.
              </div>
            </div>
          ) : (
            <InlineError failure={verify.state.failure} />
          )}
          <InlineError failure={resend.state.failure} />

          <OtpInput
            value={code}
            onChange={(next) => {
              setCode(next)
              verify.reset()
            }}
            onComplete={() => {
              void onSubmit()
            }}
            invalid={wrong}
            disabled={verify.pending}
          />
        </div>
        <div className="ar-card__footer" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          {expired ? null : (
            <Button
              tone="primary"
              size="lg"
              block
              type="submit"
              busy={verify.pending}
              busyLabel="Checking the code"
              disabled={code.length !== CODE_LENGTH}
            >
              Verify
            </Button>
          )}
          <Button
            tone="ghost"
            block
            busy={resend.pending}
            busyLabel="Sending a new code"
            disabled={cooldown > 0}
            onClick={() => {
              void onResend()
            }}
          >
            {cooldown > 0 ? `Send a new code in ${formatCooldown(cooldown)}` : 'Send a new code'}
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}

/** Mirrors the resend cooldown in `app/security/otp.py`. A floor, not the authority. */
const RESEND_COOLDOWN_SECONDS = 45

function formatCooldown(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  return `${String(minutes)}:${String(seconds % 60).padStart(2, '0')}`
}
